import {useResizeObserver} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import LocalStorage from 'LocalStorage';
import {SidepanelSize, sizeToWidth} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

function useDocumentListener<K extends keyof DocumentEventMap>(
  event: K,
  handler: ((e: DocumentEventMap[K]) => void) | null
): void {
  useEffect(() => {
    if (handler == null) {
      return;
    }
    document.addEventListener(event, handler);
    return () => {
      document.removeEventListener(event, handler);
    };
  }, [event, handler]);
}

const DEFAULT_MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.9;
const DEFAULT_FALLBACK_WIDTH = 640;

function getDefaultWidthForSize(size: SidepanelSize): number {
  const w = sizeToWidth[size];
  return typeof w === 'number' ? w : DEFAULT_FALLBACK_WIDTH;
}

/**
 * Clamps a panel width into the resizable range.
 *
 * `minWidth` is applied last so that it wins when the viewport is too narrow to honor both bounds: a panel
 * that overflows a very small viewport is better than one collapsed below the width its content needs.
 */
function clampWidth(width: number, {minWidth, maxWidth}: {minWidth: number; maxWidth: number}): number {
  return Math.max(minWidth, Math.min(maxWidth, width));
}

interface UseResizableSidepanelOptions {
  storageKey: string;
  defaultPanelSize: SidepanelSize;
  /** Minimum panel width in pixels when resizing. Defaults to 320. */
  minWidth?: number;
}

interface UseResizableSidepanelResult {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  panelSize: SidepanelSize;
  onPanelSizeChange: (size: SidepanelSize) => void;
}

export function useResizableSidepanel({
  storageKey,
  defaultPanelSize,
  minWidth = DEFAULT_MIN_WIDTH,
}: UseResizableSidepanelOptions): UseResizableSidepanelResult {
  const [panelSize, setPanelSize] = useState<SidepanelSize>(defaultPanelSize);
  const enabled = panelSize !== SidepanelSize.FULL;
  // A caller can pass a minWidth larger than its defaultPanelSize's width. The minimum wins, otherwise the
  // panel would open narrower than a width the user cannot drag back down to.
  const defaultWidth = Math.max(minWidth, getDefaultWidthForSize(defaultPanelSize));
  const [containerRef, containerRect] = useResizeObserver<HTMLDivElement>();

  const [width, setWidth] = useState<number>(() => {
    if (!enabled) {
      return defaultWidth;
    }
    const stored = LocalStorage.getItem(storageKey);
    if (stored != null) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed >= minWidth) {
        return parsed;
      }
    }
    return defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const prevEnabledRef = useRef(enabled);

  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      const stored = LocalStorage.getItem(storageKey);
      if (stored != null) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed) && parsed >= minWidth) {
          setWidth(parsed);
        }
      }
    }
    prevEnabledRef.current = enabled;
  }, [enabled, minWidth, storageKey]);

  const getMaxWidth = useCallback((): number => {
    const effectiveWidth = containerRect.width || window.innerWidth;
    return effectiveWidth * MAX_WIDTH_RATIO;
  }, [containerRect.width]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      // The panel is anchored to the right edge, so dragging the divider left (decreasing clientX) widens it.
      const delta = startXRef.current - e.clientX;
      setWidth(clampWidth(startWidthRef.current + delta, {minWidth, maxWidth: getMaxWidth()}));
    },
    [getMaxWidth, minWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useDocumentListener('mousemove', isResizing ? handleMouseMove : null);
  useDocumentListener('mouseup', isResizing ? handleMouseUp : null);

  useEffect(() => {
    if (!isResizing) {
      return;
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (containerRect.width > 0) {
      const maxWidth = containerRect.width * MAX_WIDTH_RATIO;
      setWidth((prev) => clampWidth(prev, {minWidth, maxWidth}));
      return;
    }
    const handleWindowResize = (): void => {
      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      setWidth((prev) => clampWidth(prev, {minWidth, maxWidth}));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [enabled, containerRect.width, minWidth]);

  useEffect(() => {
    if (!enabled || isResizing) {
      return;
    }
    LocalStorage.setItem(storageKey, String(width));
  }, [width, storageKey, enabled, isResizing]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) {
        return;
      }
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setIsResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [enabled, width]
  );

  return {width, isResizing, handleMouseDown, containerRef, panelSize, onPanelSizeChange: setPanelSize};
}
